// Continue the callback on the guest's own phone (route /callback/:callId, roadmap #61).
//
// A public screen has no keyboard, so the no-staff-available screen offers a code the
// guest scans to land HERE, on their own phone, and type their contact details with a
// real keyboard. Everything else is the same as the on-screen form in StaffNotAvailable:
// the same validation, the same realtime record for backward compatibility with the old
// client, and the same POST that routes the request to the staff assigned to the QR (#53).
//
// The call identifier travels in the path rather than in navigation state, because this
// screen is reached from a different device, with no history behind it.
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandLockup from '../components/BrandLockup'
import { updateCallRealTime } from '../core/callStatus'
import { httpRequest } from '../core/http'
import { t } from '../i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_RE = /^\+?[0-9 ()-]{6,18}$/

const CallbackOnPhone = () => {
  const { callId } = useParams()

  const [contactMethod, setContactMethod] = useState<'' | 'email' | 'contact_no'>('')
  const [contactValue, setContactValue] = useState('')
  const [error, setError] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = contactValue.trim()
    if (contactMethod === 'email' && !EMAIL_RE.test(value)) {
      setError(t.invalidEmail)
      return
    }
    if (contactMethod === 'contact_no' && !PHONE_RE.test(value)) {
      setError(t.invalidPhone)
      return
    }
    setError('')

    const values = {
      email: contactMethod === 'email' ? value : '',
      telephone: contactMethod === 'contact_no' ? value : '',
    }
    // Keep the realtime record for backward compatibility with the old client.
    await updateCallRealTime(callId, { callbackcontact: values })
    // Route the request to the QR's assigned staff by email (issue #53). Best-effort:
    // the guest gets the confirmation regardless of the notification result.
    httpRequest('callback-request', 'POST', {
      call_id: callId,
      telephone: values.telephone,
      email: values.email,
    })
    setIsSubmitted(true)
  }

  // Reached without a call identifier: the link was mistyped or truncated. Say so
  // plainly rather than silently registering a request nobody can act on.
  if (!callId) {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <div className="cf-icon">🔗</div>
          <h1 className="cf-title">{t.callbackPhoneTitle}</h1>
          <p className="cf-subtitle">{t.callbackPhoneInvalidLink}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="cf-page">
      <div className="cf-card">
        <BrandLockup />
        <div className="cf-icon">✍️</div>
        <h1 className="cf-title">{t.callbackPhoneTitle}</h1>

        {isSubmitted ? (
          <p className="cf-subtitle">{t.callbackThanks}</p>
        ) : (
          <>
            <p className="cf-subtitle">{t.callbackPhoneIntro}</p>
            <p className="cf-subtitle">{t.callbackPreferredMethod}</p>
            <div className="cf-radio-group">
              <label className={`cf-radio ${contactMethod === 'email' ? 'selected' : ''}`}>
                <input type="radio" name="contactMethod" value="email" onChange={() => setContactMethod('email')} />
                {t.emailLabel}
              </label>
              <label className={`cf-radio ${contactMethod === 'contact_no' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="contactMethod"
                  value="contact_no"
                  onChange={() => setContactMethod('contact_no')}
                />
                {t.phoneLabel}
              </label>
            </div>

            {contactMethod && (
              <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                <div className="cf-field">
                  <label htmlFor="cf-contact">{contactMethod === 'email' ? t.emailLabel : t.phoneLabel}</label>
                  <input
                    id="cf-contact"
                    type={contactMethod === 'email' ? 'email' : 'tel'}
                    inputMode={contactMethod === 'contact_no' ? 'tel' : undefined}
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                  />
                  {error && <span className="cf-error">{error}</span>}
                </div>
                <button type="submit" className="cf-btn cf-btn-primary">
                  {t.send}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CallbackOnPhone
